/**
 * Сховище чернеток форм (UX-26): `sessionStorage` вкладки, лише за п'яти умов Security
 * (states.md, розд. 7):
 * 1) ключ `draft:<userId>:<sessionId>:<форма>:<recordId>`; на старті й після входу чужі ключі
 *    видаляються без показу (`purgeDrafts`, `setDraftUser`, `rotateDraftSession`);
 * 2) очищення: після збереження (`clearDraft`), під час виходу в усіх вкладках (`signOutDrafts`
 *    → `BroadcastChannel`) і на будь-яку відповідь 401 (`clearAllDrafts`, підписка в main.tsx);
 * 3) строк 12 год: старші видаляються без показу;
 * 4) без полів `sensitive` і секретів: їх відкидає форма до запису (`useDraft`, docs/dev/web-shell.md);
 * 5) нікуди за межі вкладки: не логуємо, не шлемо в аналітику, не пишемо в інші сховища.
 *
 * Модуль бере перший екран (очищення на старті), тож він має лишатися крихітним. Документ входу
 * бере лише `draft-session.ts`.
 */
import {
  clearAllDrafts,
  DRAFT_PREFIX,
  DRAFT_SID_KEY,
  DRAFT_USER_KEY,
  draftKeys,
  draftStorage,
  rotateDraftSession,
} from './draft-session.ts';

export { clearAllDrafts, rotateDraftSession };

export const DRAFT_TTL_MS = 12 * 3_600_000;
/** Одна чернетка — до 20 КБ у UTF-8: запис синхронний, великий блокує набір на слабкому ПК. */
export const DRAFT_MAX_BYTES = 20 * 1024;
/** Не більше 50 чернеток на вкладку: найстаріші витісняються. */
export const DRAFT_MAX_COUNT = 50;

const PREFIX = DRAFT_PREFIX;
const SID_KEY = DRAFT_SID_KEY;
const USER_KEY = DRAFT_USER_KEY;
/** Без входу (ще не відомо, хто) власника немає: чернетки вкладки — «локальні». */
const LOCAL_USER = 'local';
/**
 * Канал виходу між вкладками: чернетки слухають його тут, решту виходу (кеш, сховища, перехід на
 * вхід) — `platform/sign-out.ts`.
 */
export const SIGN_OUT_CHANNEL = 'clientline.session';

export interface DraftEntry<T> {
  /** Час останнього запису чернетки. */
  ts: number;
  value: T;
  /** Версія запису, від якої почали редагувати: інша версія — «Чернетка може бути застарілою». */
  version?: number;
}

const storage = draftStorage;

function sessionId(store: Storage): string {
  let sid = store.getItem(SID_KEY);
  if (!sid) {
    sid = Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('');
    store.setItem(SID_KEY, sid);
  }
  return sid;
}

function ownPrefix(store: Storage): string {
  return `${PREFIX}${store.getItem(USER_KEY) ?? LOCAL_USER}:${sessionId(store)}:`;
}

function readEntry(store: Storage, key: string): DraftEntry<unknown> | null {
  try {
    const entry = JSON.parse(store.getItem(key) ?? 'null') as DraftEntry<unknown> | null;
    return entry && typeof entry.ts === 'number' ? entry : null;
  } catch {
    return null;
  }
}

/** Ключ чернетки форми `form` для запису `recordId` (`new` — форма створення). */
export function draftKey(form: string, recordId: string): string {
  const store = storage();
  try {
    return `${store ? ownPrefix(store) : `${PREFIX}${LOCAL_USER}:-:`}${form}:${recordId}`;
  } catch {
    return `${PREFIX}${LOCAL_USER}:-:${form}:${recordId}`;
  }
}

/** Видалити чужі (інший користувач чи сесія) і прострочені чернетки — умови 1 і 3. */
export function purgeDrafts(now = Date.now()): void {
  const store = storage();
  if (!store) return;
  try {
    const mine = ownPrefix(store);
    for (const key of draftKeys(store)) {
      const entry = key.startsWith(mine) ? readEntry(store, key) : null;
      if (!entry || now - entry.ts > DRAFT_TTL_MS) store.removeItem(key);
    }
  } catch {
    // Сховище недоступне — нічого прибирати.
  }
}

export function loadDraft<T>(key: string, now = Date.now()): DraftEntry<T> | null {
  const store = storage();
  if (!store) return null;
  try {
    const entry = readEntry(store, key);
    if (entry && now - entry.ts > DRAFT_TTL_MS) {
      store.removeItem(key);
      return null;
    }
    return entry as DraftEntry<T> | null;
  } catch {
    return null;
  }
}

let suspended = false;

/**
 * Сесія спливла, а повторного входу поверх сторінки ще не було (E3a-06b, умова 3 Security):
 * чернетки вкладки вже стерто, і нових не пишемо — введене живе лише в пам'яті відкритої форми.
 * Після входу тим самим користувачем запис відновлюється під новою сесією чернеток
 * (`platform/relogin.ts`: `rotateDraftSession` → `setDraftUser`).
 */
export function suspendDrafts(on: boolean): void {
  suspended = on;
}

/**
 * Записати чернетку. `false` — не збережено (завелика, сховище переповнене чи недоступне, запис
 * призупинено): тоді введене живе лише в пам'яті форми, і захист від втрати вважає зміни
 * незахищеними (UX-27).
 */
export function saveDraft<T>(
  key: string,
  value: T,
  options: { version?: number; now?: number } = {},
): boolean {
  const store = storage();
  if (!store || suspended) return false;
  try {
    const entry: DraftEntry<T> = { ts: options.now ?? Date.now(), value };
    if (options.version !== undefined) entry.version = options.version;
    const json = JSON.stringify(entry);
    if (new TextEncoder().encode(json).length > DRAFT_MAX_BYTES) {
      store.removeItem(key);
      return false;
    }
    if (store.getItem(key) === null) {
      const mine = ownPrefix(store);
      const own = draftKeys(store)
        .filter((k) => k.startsWith(mine))
        .map((k) => [k, readEntry(store, k)?.ts ?? 0] as const)
        .sort((a, b) => a[1] - b[1]);
      for (const [old] of own.slice(0, Math.max(0, own.length - DRAFT_MAX_COUNT + 1))) {
        store.removeItem(old);
      }
    }
    store.setItem(key, json);
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // нічого
  }
}

/**
 * Власник чернеток після входу — id з `GET /api/v1/me`. Інший власник у тій самій вкладці — усі
 * чернетки вкладки видаляються без показу (умова 1).
 */
export function setDraftUser(userId: string): void {
  const store = storage();
  if (!store) return;
  try {
    if (store.getItem(USER_KEY) === userId) return;
    clearAllDrafts();
    store.setItem(USER_KEY, userId);
  } catch {
    return;
  }
  // Відкрита форма перепише свою чернетку під новим ключем (ім'я могло прийти після відкриття).
  for (const listener of ownerListeners) listener();
}

const ownerListeners = new Set<() => void>();

/** Підписка форми на зміну власника чернеток. */
export function onDraftOwnerChange(listener: () => void): () => void {
  ownerListeners.add(listener);
  return () => ownerListeners.delete(listener);
}

function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SIGN_OUT_CHANNEL);
}

/**
 * Вихід: чернетки цієї й усіх інших вкладок. Подія `storage` для `sessionStorage` між вкладками
 * не приходить, тому — `BroadcastChannel`. Викликає `signOut` (`platform/sign-out.ts`, пункт «Вийти»
 * меню користувача).
 */
export function signOutDrafts(): void {
  rotateDraftSession();
  const ch = channel();
  ch?.postMessage({ type: 'sign-out' });
  ch?.close();
}

/** Старт застосунку: прибрати чужі й прострочені, слухати вихід з інших вкладок. */
export function initDrafts(): () => void {
  purgeDrafts();
  const ch = channel();
  if (!ch) return () => undefined;
  ch.onmessage = (event: MessageEvent<{ type?: string }>) => {
    if (event.data?.type === 'sign-out') rotateDraftSession();
  };
  return () => ch.close();
}
